import { toByteArray, fromByteArray } from 'base64-js';
import {
  ThreeDSecureAdditionalInformation,
  ThreeDSecureBillingAddress,
  ThreeDSecureShippingAddress,
} from 'braintree-web/three-d-secure';
import dayjs, { extend } from 'dayjs';
import dayjsUtcPlugin from 'dayjs/plugin/utc';

import {
  Additional3dsInfo,
  Address,
  BraintreeAddressMatch,
  BraintreeAuthenticationIndicator,
  BraintreeCustomFields,
  BraintreeShippingNameIndicator,
  CreditCardType,
  FraudPreventionRecommendation,
  MaybeNullable,
} from '@types-internal';

extend(dayjsUtcPlugin);

/**
 * Get chunks of text
 * If you want to get first X letters: chunks(test, X)
 * If you want to skip first Y letters, then keep X letters: chunks(test, X, Y)
 * @param text text to slice
 * @param chunkSize length of text to keep
 * @param chunkIndex length of text to skip
 * @return slice of text
 */
export const chunks = (text?: string, chunkSize = 50, chunkIndex = 0): string | undefined => {
  return text
    ? text.slice(chunkIndex * chunkSize, (chunkIndex + 1) * chunkSize) || undefined
    : undefined;
};

/**
 * Get only digits from text (ignore any other character)
 * @param text text to format
 * @return digits from text
 */
export const digitsOnly = (text?: string | null): string | undefined => {
  if (!text) {
    return undefined;
  }
  return text.replaceAll(/[^\d]/g, '');
};

const convertDate = (timestamp?: number): string | undefined => {
  if (!timestamp) {
    return undefined;
  }
  const date = dayjs(timestamp).format('YYYYMMDD');
  if (date === 'Invalid Date') {
    return undefined;
  }
  return date;
};

/**
 * Convert an address from zooplus format to Braintree format
 * @param type type of the address (shipping or billing)
 * @param address address to convert
 * @return digits from text
 */
export function convertAddress(
  type: 'shipping',
  address?: Address,
): ThreeDSecureShippingAddress | undefined;
export function convertAddress(
  type: 'billing',
  address?: Address,
): ThreeDSecureBillingAddress | undefined;
export function convertAddress(
  type: 'shipping' | 'billing',
  address?: Address,
): ThreeDSecureBillingAddress | ThreeDSecureShippingAddress | undefined {
  if (!address) {
    return undefined;
  }

  if (type === 'shipping') {
    return buildShippingAddress(address);
  }
  return buildBillingAddress(address);
}

// Braintree limits each address line to 50 characters.
const ADDRESS_LINE_MAX_LENGTH = 50;

// The three street lines are shared between shipping and billing addresses.
const buildAddressStreetLines = (address: Address) => ({
  streetAddress: chunks(address.street, ADDRESS_LINE_MAX_LENGTH, 0), // Line 1 (eg. number, street, etc).
  extendedAddress: chunks(address.street, ADDRESS_LINE_MAX_LENGTH, 1), // Line 2 (eg. suite, apt #, etc.).
  line3: chunks(address.street, ADDRESS_LINE_MAX_LENGTH, 2), // Line 3 if needed (eg. suite, apt #, etc).
});

const buildShippingAddress = (address: Address): ThreeDSecureShippingAddress => {
  const streetLines = buildAddressStreetLines(address);
  return {
    streetAddress: streetLines.streetAddress || '', // Line 1 of the shipping address (eg. number, street, etc). (maximum length 50)
    extendedAddress: streetLines.extendedAddress || '', // Line 2 of the shipping address (eg. suite, apt #, etc.). (maximum length 50)
    line3: streetLines.line3 || '', // Line 3 of the shipping address if needed (eg. suite, apt #, etc). (maximum length 50)
    locality: chunks(address.city) || '', // The locality (city) name associated with the shipping address.
    region: address.country, // This field expects an ISO3166-2 subdivision code. The subdivision code is what follows the hyphen separator in the full ISO 3166-2 code. For example, the state of Ohio in the United States we expect "OH" as opposed to the full ISO 3166-2 code "US-OH".
    postalCode: chunks(address.postalCode, 10) || '', // The zip code or equivalent for countries that have them.
    countryCodeAlpha2: chunks(address.isoCountry, 2) || '', // The 2 character country code.
  };
};

const buildBillingAddress = (address: Address): ThreeDSecureBillingAddress => {
  const streetLines = buildAddressStreetLines(address);
  return {
    streetAddress: streetLines.streetAddress, // Line 1 of the shipping address (eg. number, street, etc). (maximum length 50)
    extendedAddress: streetLines.extendedAddress, // Line 2 of the shipping address (eg. suite, apt #, etc.). (maximum length 50)
    line3: streetLines.line3, // Line 3 of the shipping address if needed (eg. suite, apt #, etc). (maximum length 50)
    locality: address.city, // The locality (city) name associated with the shipping address.
    region: address.country, // This field expects an ISO3166-2 subdivision code. The subdivision code is what follows the hyphen separator in the full ISO 3166-2 code. For example, the state of Ohio in the United States we expect "OH" as opposed to the full ISO 3166-2 code "US-OH".
    postalCode: address.postalCode, // The zip code or equivalent for countries that have them.
    countryCodeAlpha2: address.isoCountry, // The 2 character country code.
    givenName: chunks(address.firstName), // The first name associated with the billing address. (maximum length 50)
    surname: chunks(address.lastName), // The last name associated with the billing address. (maximum length 50)
    phoneNumber: digitsOnly(address.phoneNumber), // The phone number associated with the billing address. Only numbers; remove dashes, parenthesis and other characters.
  };
};

const compareCardShippingNames = (
  shippingAddress: Address,
  creditCardHolderName?: string,
): BraintreeShippingNameIndicator | undefined => {
  if (!creditCardHolderName) {
    return undefined;
  }

  const shippingName = `${shippingAddress.firstName} ${shippingAddress.lastName}`;

  if (creditCardHolderName === shippingName) {
    return BraintreeShippingNameIndicator.ACCOUNT_AND_SHIPPING_NAME_IDENTICAL_01;
  }
  return BraintreeShippingNameIndicator.ACCOUNT_AND_SHIPPING_NAME_DIFFERENT_02;
};

// Fields that must all be equal for a billing and shipping address to be considered identical.
const ADDRESS_MATCH_FIELDS: (keyof Address)[] = [
  'firstName',
  'lastName',
  'street',
  'company',
  'city',
  'isoCountry',
  'country',
  'postalCode',
  'phoneNumber',
];

const compareBillingShippingAddress = (
  billingAddress: Address,
  shippingAddress: Address,
): BraintreeAddressMatch | undefined => {
  if (!billingAddress || !shippingAddress) {
    return undefined;
  }

  const addressesAreIdentical = ADDRESS_MATCH_FIELDS.every(
    field => billingAddress[field] === shippingAddress[field],
  );

  if (addressesAreIdentical) {
    return BraintreeAddressMatch.BILLING_AND_SHIPPING_ADDRESS_IDENTICAL_01;
  }
  return BraintreeAddressMatch.BILLING_AND_SHIPPING_ADDRESS_DIFFERENT_02;
};

/**
 * Format additional information in correct Braintree format
 * See Braintree docs: https://braintree.github.io/braintree-web/current/ThreeDSecure.html#~additionalInformation
 * @param isSubscriptionFirst true if payment is the first of a subscription
 * @param subscriptionFirstInterval number of days of the subscription
 * @param raw object with info from checkout to format
 * @return formatted additional information
 */
export const formatAdditionalInformation = (
  isSubscriptionFirst = false,
  subscriptionFirstInterval?: string,
  raw?: Additional3dsInfo,
): ThreeDSecureAdditionalInformation | undefined => {
  const minimalAdditionalInformation = {
    authenticationIndicator: isSubscriptionFirst // The 2-digit number indicating the type of authentication request
      ? BraintreeAuthenticationIndicator.RECURRING_TRANSACTION_02
      : BraintreeAuthenticationIndicator.PAYMENT_01,
    recurringFrequency: isSubscriptionFirst
      ? chunks(digitsOnly(subscriptionFirstInterval), 3)
      : undefined, // Integer value indicating the minimum number of days between recurring authorizations. A frequency of monthly is indicated by the value 28. Multiple of 28 days will be used to indicate months (ex. 6 months = 168). (maximum length 3) Also note that maximum frequency allowed at zooplus currently is 16 weeks so 112 days.
    recurringEnd: isSubscriptionFirst ? dayjs.utc().add(100, 'year').format('YYYYMMDD') : undefined, // The 8-digit number (format: YYYYMMDD) indicating the date after which no further recurring authorizations should be performed
    purchaseDate: dayjs.utc().format('YYYYMMDDHHmmss'), // The 14-digit number (format: YYYYMMDDHHMMSS) indicating the date in UTC of original purchase.
    productCode: 'GEN', // The 3-letter string representing the merchant product code. Possible values: GEN for General Retail.
  };

  if (!raw) {
    return minimalAdditionalInformation;
  }

  return {
    ...minimalAdditionalInformation,
    shippingAddress: convertAddress('shipping', raw.shippingAddress), // The shipping address
    shippingGivenName: chunks(raw.shippingAddress.firstName), // The first name associated with the shipping address. (maximum length 50)
    shippingSurname: chunks(raw.shippingAddress.lastName), // The last name associated with the shipping address. (maximum length 50)
    shippingPhone: chunks(digitsOnly(raw.shippingAddress.phoneNumber), 20), // The phone number associated with the shipping address. Only numbers; remove dashes, parenthesis and other characters. (maximum length 20)
    accountCreateDate: convertDate(raw.accountCreateDate), // The 8-digit number (format: YYYYMMDD) indicating the date the cardholder opened the account.
    shippingNameIndicator: compareCardShippingNames(raw.shippingAddress, raw.creditCardHolderName), // The 2-digit value indicating if the cardholder name on the account is identical to the shipping name used for the transaction. Possible values: 01/02
    paymentAccountAge: convertDate(raw.creditCardCreateDate), // The 8-digit number (format: YYYYMMDD) indicating the date the payment account was added to the cardholder account.
    addressMatch: compareBillingShippingAddress(raw.billingAddress, raw.shippingAddress), // The 1-character value (Y/N) indicating whether cardholder billing and shipping addresses match.
    accountId: chunks(raw.accountId.toString(), 64), // Additional cardholder account information. (maximum length 64)
    ipAddress: raw.ipAddress, // The IP address of the consumer. IPv4 and IPv6 are supported.
    orderDescription: chunks(raw.articleIds.join(','), 256), // Brief description of items purchased. (maximum length 256)
    userAgent: chunks(raw.userAgent, 500), // The exact content of the HTTP user agent header. (maximum length 500)
  };
};

export const formatCustomFields = (
  cardType?: CreditCardType,
  isSubscriptionFirst = false,
  customerId?: string,
  fraudPreventionRecommendation?: FraudPreventionRecommendation | undefined,
  force3dsChallengeEnabled?: 'true' | 'false',
): BraintreeCustomFields => {
  const customFields = {
    card_type: cardType ?? 'ONE_TIME',
    subscription_first: String(isSubscriptionFirst),
    ...(customerId && { customer_id: customerId }),
    fraud_prevention_recommendation: fraudPreventionRecommendation,
    force_3ds_challenge_enabled: force3dsChallengeEnabled ?? 'false',
  };

  return customFields;
};

export const base64JsonToJS = <T>(data: MaybeNullable<string>) => {
  const jsonAsString = data && data !== '' && base64ToString(data);
  return (jsonAsString && (JSON.parse(jsonAsString) as T)) || undefined;
};

export const jsToBase64Json = (data: NonNullable<unknown>) => stringToBase64(JSON.stringify(data));

export const stringToBase64 = (data: string) => {
  const utf8TextEncoder = new TextEncoder();
  return fromByteArray(utf8TextEncoder.encode(data));
};

export const stringUriToBase64 = (data: string) =>
  stringToBase64(data).replaceAll('/', '_').replaceAll('+', '-').replaceAll('=', '');

export const base64ToString = (data: string) => {
  const utf8TextDecoder = new TextDecoder();
  return utf8TextDecoder.decode(toByteArray(data));
};
